import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FlightAccess } from '@/types/flightAccess';
import { fetchFlightAccesses } from './api';

/** What one completed load produced, tagged with the load it answered. */
interface Outcome {
  nonce: number;
  accesses: FlightAccess[];
  failed: boolean;
}

export interface FlightAccessesState {
  /**
   * The SAVED access of each user that has one, by user id.
   *
   * A user missing from it has never been restricted and sees every flight. Read it
   * through `hasLoaded` first: before the list arrives, and after it fails, a missing
   * entry means "not known", not "every flight".
   */
  byUser: ReadonlyMap<number, FlightAccess>;
  /** True once a load has succeeded — the map can then be read as the answer. */
  hasLoaded: boolean;
  isLoading: boolean;
  hasFailed: boolean;
  reload: () => void;
  /** Folds an access a write just returned back into the map. */
  apply: (access: FlightAccess) => void;
}

/**
 * Every saved flight access, for the user list.
 *
 * Its own request beside `/user/all` rather than part of it: the user endpoint knows
 * nothing about access, and the API guide's answer is to load both once and join them
 * on the user id. Failing apart is the point — a list of users whose access could not
 * be read is still a working list, it just cannot say who sees what.
 *
 * **Loading is derived, not stored** — the outcome is tagged with the load it
 * answered, as in `useUsers`.
 */
export const useFlightAccesses = (): FlightAccessesState => {
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchFlightAccesses(controller.signal)
      .then((accesses) => setOutcome({ nonce, accesses, failed: false }))
      .catch(() => {
        if (controller.signal.aborted) return;
        setOutcome({ nonce, accesses: [], failed: true });
      });

    return () => controller.abort();
  }, [nonce]);

  const reload = useCallback(() => setNonce((current) => current + 1), []);

  const hasList = outcome !== null && !outcome.failed;

  const apply = useCallback(
    (saved: FlightAccess) => {
      // Nothing trustworthy to fold into: the first load may have left before this
      // write, or it failed. A fresh load is guaranteed to include what was saved.
      if (!hasList) {
        setNonce((current) => current + 1);
        return;
      }

      setOutcome((current) => {
        if (!current) return current;

        const others = current.accesses.filter((access) => access.userId !== saved.userId);
        return { ...current, accesses: [...others, saved] };
      });
    },
    [hasList],
  );

  const accesses = outcome?.accesses;

  const byUser = useMemo(
    () => new Map((accesses ?? []).map((access) => [access.userId, access] as const)),
    [accesses],
  );

  const isCurrent = outcome?.nonce === nonce;

  return {
    byUser,
    hasLoaded: hasList,
    isLoading: !isCurrent,
    hasFailed: isCurrent && outcome.failed,
    reload,
    apply,
  };
};
